import { AxiosError } from 'axios';
import { ISetError, SET_ERROR } from '../types';
import { UNKNOWN_ERROR_MESSAGE } from './constants';

/**
 * Normalizes different error types into a consistent error message string
 * @param error - The error to normalize, can be either an AxiosError or standard Error
 * @returns A string containing the normalized error message
 *
 * If the error is an AxiosError with a response, returns the response data
 * If the error is a standard Error, returns the error message
 * Otherwise returns the default unknown error message
 */
export const normalizeError = (error: unknown) => {
    let errorMessage = UNKNOWN_ERROR_MESSAGE;

    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as AxiosError;
        errorMessage =
            axiosError.response?.data?.error || axiosError.response?.data;
    } else if (error instanceof Error) errorMessage = error?.message;
    return errorMessage;
};

/**
 * Handles errors by displaying a snackbar notification and returning an error action
 * @param error - The error to handle, either an AxiosError or standard Error
 * @param messageAlertFunction - Function to display error message in UI, typically via snackbar
 * @returns An action object with type SET_ERROR and the normalized error message as payload
 *
 * This function:
 * 1. Takes an error and message display function as parameters
 * 2. Normalizes the error message using normalizeError()
 * 3. Returns a Redux-style action with the error message
 */
export const handleError = (
    error: AxiosError | Error,
    messageAlertFunction?: (message: string) => void
): ISetError => {
    const errorMessage = normalizeError(error);
    if (messageAlertFunction) messageAlertFunction(errorMessage);
    return { type: SET_ERROR, payload: errorMessage };
};
